<!-- ConfirmDialog.vue - Reusable modal dialog driven by useDialogStore -->
<template>
  <Teleport to="body">
    <Transition name="dialog-fade">
      <div
        v-if="dialogStore.visible"
        class="dialog-overlay"
        @click.self="dialogStore.onCancel"
        @keydown.esc="dialogStore.onCancel"
      >
        <div class="dialog-box" role="dialog" :aria-labelledby="titleId" :aria-describedby="msgId">
          <!-- Header -->
          <div class="dialog-header">
            <span :id="titleId" class="dialog-title">{{ dialogStore.title }}</span>
          </div>

          <!-- Body -->
          <div class="dialog-body">
            <p :id="msgId" class="dialog-message">{{ dialogStore.message }}</p>
            <p v-if="dialogStore.subMessage" class="dialog-sub-message">{{ dialogStore.subMessage }}</p>
          </div>

          <!-- Buttons -->
          <div class="dialog-buttons">
            <button class="btn-confirm" @click="dialogStore.onConfirm">
              {{ dialogStore.confirmText }}
            </button>
            <button class="btn-cancel" @click="dialogStore.onCancel">
              {{ dialogStore.cancelText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { useDialogStore } from '../stores/useDialogStore.js';

export default {
  name: 'ConfirmDialog',

  setup() {
    const dialogStore = useDialogStore();
    return { dialogStore };
  },

  computed: {
    titleId() { return 'confirm-dialog-title'; },
    msgId()   { return 'confirm-dialog-message'; }
  }
};
</script>

<style scoped lang="scss">
@use '@/styles/utils' as *;

// Overlay
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-index-modal;
}

// Dialog box — matches Figma spec (500 × auto, white, shadow, 5px radius)
.dialog-box {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0 0 24px;
  gap: 18px;

  width: 500px;
  max-width: calc(100vw - 32px);

  background: #FFFFFF;
  box-shadow: 0px 4px 8.9px rgba(0, 0, 0, 0.5);
  border-radius: 5px;
  overflow: hidden; // clip header corners
}

// Header
.dialog-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 24px;
  gap: 10px;

  width: 100%;
  min-height: 40px;
  box-sizing: border-box;

  background: $brand-primary;
}

.dialog-title {
  font-family: $font-family-base;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #FFFFFF;
}

// Body
.dialog-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 24px;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
}

.dialog-message,
.dialog-sub-message {
  width: 100%;
  font-family: $font-family-base;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: #000000;
  margin: 0;
}

// Buttons row
.dialog-buttons {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0 24px;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
}

// Confirm button (filled) — uses brand-primary to match project theme (green for MLP, blue for COMDA)
.btn-confirm {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;

  min-width: 96px;
  height: 37px;

  background: $brand-primary;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  font-family: $font-family-base;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: #FFFFFF;

  transition: background-color 0.2s ease;

  &:hover {
    background: $brand-primary-dark;
  }

  &:active {
    background: $brand-primary-dark;
    opacity: 0.9;
  }
}

// Cancel button (text only) — uses brand-primary for text color
.btn-cancel {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;

  min-width: 82px;
  height: 37px;

  background: transparent;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  font-family: $font-family-base;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: $brand-primary;

  transition: background-color 0.2s ease;

  &:hover {
    background: $brand-primary-lighter;
  }
}

// Fade transition
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}
</style>
